// frontend/src/components/formularios/FormularioBase.jsx
// FORMULARIO BASE GENÉRICO PARA CRUD
import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { PrimaryButton as SharedPrimary, SecondaryButton as SharedSecondary, NavButton, DuplicarButton } from '../common/Buttons';
import { FilaBotonesPie, BotonCancelarPie, BotonGuardarPie } from '../common/BotonesPieFormulario';
import apiClient from '../../services/apiClient';
import ConfiguracionBase from '../configuracion/ConfiguracionBase';
import EscanerQR from '../../pages/PortalDespacho/EscanerQR';
import SearchableCombo from '../common/SearchableCombo';
import { useAutoFocoPrimerCampo } from '../../hooks/useNavegacionTeclado';
import { parseEquipoIdDesdeQR } from '../../services/despachoCombustibleService';

// Simple in-memory cache for select options to avoid refetching the same endpoint

// STYLED COMPONENTS
const FormContainer = styled.div`
  max-width: ${({ $compact, $fullWidth }) => $fullWidth ? '100%' : ($compact ? '720px' : '900px')};
  margin: ${({ $fullWidth }) => $fullWidth ? '0' : '0 auto'};
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};
  max-height: 100%;
  /* If fullWidth, remove card-like chrome to integrate with page */
  ${({ $fullWidth, theme }) => ($fullWidth ? `box-shadow: none; border-radius: 0; background: ${theme.colors.transparent};` : '')}
`;

const FormTitle = styled.h4`
  margin-bottom: 2rem;
  color: ${({ theme }) => theme.colors.txtPrincipal};
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  
  i {
    color: ${({ theme }) => theme.colors.amarillo};
    margin-right: 0.5rem;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1rem;
`;

const ScrollArea = styled.div`
  padding: ${({ $compact }) => $compact ? '0.75rem 1rem' : '1.25rem 1.5rem'};
  overflow: auto;
  flex: 1 1 auto;
  max-height: 100%;
  min-height: 0;
  padding-bottom: ${({ $compact }) => $compact ? '1rem' : '1.25rem'};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.txtSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  display: block;
`;

const InlineRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const AddSelectButton = styled.button`
  width: 34px;
  height: 34px;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover {
    border-color: ${({ theme }) => theme.colors.info};
    color: ${({ theme }) => theme.colors.info};
    background: ${({ theme }) => `${theme.colors.info}11`};
  }
`;

// Acceso por QR a un campo `select` (hoy sólo lo usa "Equipo" en la app móvil,
// vía field.qrEscaner — ver el caso 'select' de renderField). Tamaño de tap
// target de 44px, mayor que AddSelectButton, porque es específicamente para
// tocar con el dedo en el campo, no un botón de escritorio.
const QrScanButton = styled.button`
  flex-shrink: 0;
  min-width: 44px;
  min-height: 44px;
  border: 1px solid ${({ theme }) => theme.colors.info};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => `${theme.colors.info}11`};
  color: ${({ theme }) => theme.colors.info};
  font-size: 1.2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover {
    opacity: 0.82;
  }
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid ${({ theme, $error }) => $error ? theme.colors.danger : theme.colors.borderDefault};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  transition: ${({ theme }) => theme.transitions.fast};
  
  &:focus {
  outline: none;
  border-color: ${({ theme }) => theme.colors.info} !important;
  /* subtle focus ring using info color with alpha (8-digit hex appended) */
  box-shadow: 0 0 0 2px ${({ theme }) => `${theme.colors.info}33`} !important;
  }
  &:focus-visible {
    outline: none;
    border-color: ${({ theme }) => theme.colors.info} !important;
    box-shadow: 0 0 0 2px ${({ theme }) => `${theme.colors.info}33`} !important;
  }
  
  &::placeholder {
    color: ${({ theme }) => theme.colors.txtMuted};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    background: ${({ theme }) => theme.colors.surfaceHover};
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid ${({ theme, $error }) => $error ? theme.colors.danger : theme.colors.borderDefault};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  transition: ${({ theme }) => theme.transitions.fast};
  min-height: 100px;
  resize: vertical;
  
  &:focus {
  outline: none;
  border-color: ${({ theme }) => theme.colors.info} !important;
  box-shadow: 0 0 0 2px ${({ theme }) => `${theme.colors.info}33`} !important;
  }
  &:focus-visible {
    outline: none;
    border-color: ${({ theme }) => theme.colors.info} !important;
    box-shadow: 0 0 0 2px ${({ theme }) => `${theme.colors.info}33`} !important;
  }
  
  &::placeholder {
    color: ${({ theme }) => theme.colors.txtMuted};
  }
`;

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.danger};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  margin-top: 0.25rem;
`;

const CurrentFileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 0.75rem;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.surface};
`;

const CurrentFileLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: ${({ theme }) => theme.colors.info};
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`;

const RemoveFileButton = styled.button`
  border: 1px solid ${({ theme }) => theme.colors.danger};
  color: ${({ theme }) => theme.colors.danger};
  background: ${({ theme }) => theme.colors.transparent};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: 0.4rem 0.6rem;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  cursor: pointer;
  flex-shrink: 0;
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.danger};
    color: ${({ theme }) => theme.colors.onPrimary};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  /* Con botonesMovil el padding pasa a ser el mismo en los tres formularios
     de pantalla completa de la app móvil (ver el Pie de FormularioDespachoCombustible
     y de PortalQRParteDiario) — así el pie se ve igual sin importar cuál se
     esté cargando. */
  padding: ${({ $compact, $botonesMovil }) => ($botonesMovil ? '0.85rem 1rem' : ($compact ? '0.75rem 1rem' : '1rem 2rem'))};
  /* Suma el hueco del home indicator cuando este pie queda pegado al borde
     real de la pantalla (app móvil, formularios con scroll interno). En
     desktop y en cualquier dispositivo sin muesca, env() resuelve a 0, así
     que no cambia nada ahí. */
  padding-bottom: calc(${({ $compact, $botonesMovil }) => ($botonesMovil ? '0.85rem' : ($compact ? '0.75rem' : '1rem'))} + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ theme }) => theme.colors.surface};
  flex-shrink: 0;
  position: relative;
  z-index: 1;
`;

// using shared button components from common/Buttons.jsx

// Reuse shared button styles
const PrimaryButton = SharedPrimary;
const SecondaryButton = SharedSecondary;

  // Etiqueta visible de una opción. No depende del formulario, así que vive al
// nivel del módulo y la comparten el combo y quien la necesite.
const getLabel = (opt) => {
  if (opt?.interno) {
    const detalle = [opt.descripcion, opt.marca_nombre, opt.modelo_nombre].filter(Boolean).join(' ');
    return detalle ? `${opt.interno} — ${detalle}` : opt.interno;
  }
  if (opt?.label || opt?.nombre || opt?.name) return opt.label || opt.nombre || opt.name;
  // Repuesto (p.ej. selector de fluido en lubricantes): sin campo `nombre`,
  // se arma a partir de código + marca + descripción.
  if (opt?.codigo) return [opt.codigo, opt.marca_nombre, opt.descripcion].filter(Boolean).join(' - ');
  return '';
};

// Combo de un campo `select` del formulario.
//
// IMPORTANTE: se define acá, al nivel del módulo, y NO dentro de renderField.
// Definido adentro, React veía un TIPO de componente nuevo en cada render y
// desmontaba/remontaba todos los campos select del formulario. Eso rompía cosas
// que parecían no tener relación entre sí: el foco se perdía al elegir una
// opción (el remontaje se lo llevaba al body justo después de posicionarlo),
// y cualquier referencia al nodo tomada antes del cambio de estado quedaba
// apuntando a un elemento huérfano.
const SearchableSelect = ({
  options: selOptions,
  currentValue,
  field,
  formData,
  handleChange,
  resolverOpciones,
  selectOptions,
  hasError,
}) => {
  // `open` y el texto tipeado los maneja SearchableCombo internamente.
  // Acá sólo queda lo que este campo agrega por encima: el conjunto de
  // opciones (que puede venir de la copia local, del servidor o del
  // catálogo ya cargado) y el escáner QR.
  const [items, setItems] = React.useState(selOptions);
  const [hasMore, setHasMore] = React.useState(false);
  const [searchError, setSearchError] = React.useState(false);
  const [mostrarQR, setMostrarQR] = React.useState(false);
  const [qrError, setQrError] = React.useState('');
  const debounceRef = React.useRef();
  const wrapperRef = React.useRef();

  React.useEffect(() => { setItems(selOptions); }, [selOptions]);

  const performSearch = async (q) => {
    const term = (q || '').trim();

    // Resolutor propio (app móvil): la búsqueda se hace contra la
    // copia local, no contra el servidor. Sin esto un campo marcado
    // como `searchRemote` —el de equipo, por ejemplo— queda
    // inutilizable sin señal aunque sus datos ya estén en el
    // teléfono, porque este camino no pasa por la carga inicial de
    // opciones.
    let opcionesLocales = null;
    if (typeof resolverOpciones === 'function') {
      try {
        opcionesLocales = await resolverOpciones(field.optionsEndpoint, field);
      } catch {
        // Cae al camino de siempre.
      }
    }

    if (Array.isArray(opcionesLocales)) {
      const norm = (s) => (s || '').toLowerCase();
      setItems(opcionesLocales.filter((o) => {
        const lbl = getLabel(o); const idStr = String(o.id || o.value || '');
        return norm(lbl).includes(norm(term)) || norm(idStr).includes(norm(term));
      }));
      // Se busca sobre el catálogo completo que hay en el dispositivo:
      // no hay más páginas que traer.
      setHasMore(false);
      setSearchError(false);
      return;
    }

    if (field.searchRemote) {
      try {
        let endpoint = field.optionsEndpoint || '';
        if (endpoint && !endpoint.endsWith('/')) endpoint += '/';
        const extraParams = field.name === 'sector' && formData.obra ? { obra: formData.obra } : {};
        // page_size 100 + aviso "hay más": la búsqueda cubre TODO el
        // catálogo vía servidor y nunca trunca en silencio.
        const resp = await apiClient.get(`${endpoint}`, { params: { search: term, ordering: 'interno', page_size: 100, ...extraParams } });
        const data = resp.data?.results || resp.data || [];
        setItems(Array.isArray(data) ? data : []);
        setHasMore(Boolean(resp.data?.next));
        setSearchError(false);
      } catch {
        setItems([]);
        setHasMore(false);
        // Aviso visible: un catch silencioso hacía creer que "no existe"
        setSearchError(true);
      }
    } else {
      const norm = (s) => (s || '').toLowerCase();
      const filtered = (selectOptions[field.name] || []).filter((o) => {
        const lbl = getLabel(o); const idStr = String(o.id || o.value || '');
        return norm(lbl).includes(norm(term)) || norm(idStr).includes(norm(term));
      });
      setItems(filtered);
    }
  };

  const onPick = (opt) => {
    const id = String(opt.id || opt.value || '');
    handleChange(field.name, id);
  };

  // Acceso por QR (sólo campos marcados con `field.qrEscaner`, hoy
  // "Equipo" en la app móvil — ver FormularioRegistroLubricantes).
  // Busca primero en lo ya cargado (`items`, que para este campo
  // arranca con el catálogo completo, local u online, sin depender
  // de haber escrito nada) y sólo si no aparece pide el registro
  // individual al servidor — cubre un equipo dado de alta después
  // de la última sincronización del catálogo local.
  const onDetectarQR = async (valorQR) => {
    setMostrarQR(false);
    setQrError('');
    const idDetectado = parseEquipoIdDesdeQR(valorQR);
    const texto = String(valorQR || '').trim().toLowerCase();

    const buscarEnLista = (lista) => (lista || []).find((o) => {
      const id = o.id ?? o.value;
      return (idDetectado && String(id) === String(idDetectado))
        || String(o.interno || '').toLowerCase() === texto
        || String(o.interno_alternativo || '').toLowerCase() === texto;
    });

    let encontrado = buscarEnLista(items);

    if (!encontrado && idDetectado) {
      try {
        let singleEndpoint = (field.optionsEndpoint || '').split('?')[0];
        if (singleEndpoint && !singleEndpoint.endsWith('/')) singleEndpoint += '/';
        const resp = await apiClient.get(`${singleEndpoint}${idDetectado}/`);
        encontrado = resp.data;
      } catch {
        // Sigue sin encontrarse; se informa abajo.
      }
    }

    if (!encontrado) {
      setQrError(`No se encontró ningún equipo para "${String(valorQR).trim()}".`);
      return;
    }
    onPick(encontrado);
  };

  const itemsNormalizados = (items || []).map((o) => (
    o?.id != null ? o : { ...o, id: o?.value }
  ));

  return (
    <div ref={wrapperRef}>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Combo compartido con el resto del sistema (navegación por
              flechas + Enter incluida). Antes acá vivía una copia
              propia del desplegable, que era la razón por la que este
              formulario —el de carga de partes, entre otros— no
              respondía al teclado aunque otras pantallas sí. */}
          <SearchableCombo
            // Algunas listas de opciones traen `value` en vez de `id`
            // (choices del backend). SearchableCombo indexa por `id`,
            // así que se normaliza acá y no en el componente compartido.
            items={itemsNormalizados}
            value={currentValue}
            onChange={(id) => {
              const opt = (items || []).find((o) => String(o.id || o.value) === String(id));
              if (opt) onPick(opt);
            }}
            onSearch={(term) => {
              clearTimeout(debounceRef.current);
              debounceRef.current = setTimeout(() => performSearch(term), 250);
            }}
            placeholder={`Buscar y seleccionar ${field.label}...`}
            getLabel={getLabel}
            hasMore={hasMore}
            searchError={searchError}
            error={hasError}
          />
        </div>
        {field.qrEscaner && (
          <QrScanButton type="button" tabIndex={-1} onClick={() => setMostrarQR(true)} title="Escanear QR del equipo">
            <i className="bi bi-qr-code-scan" aria-hidden="true" />
          </QrScanButton>
        )}
      </div>
      {qrError && <ErrorMessage>{qrError}</ErrorMessage>}
      {field.qrEscaner && mostrarQR && (
        <EscanerQR onDetectar={onDetectarQR} onCerrar={() => setMostrarQR(false)} />
      )}
    </div>
  );
};

const FormularioBase = ({
  title,
  icon,
  fields = [],
  initialData = {},
  apiEndpoint,
  isEditing = false,
  onSave,
  onCancel,
  validationRules = {},
  showTitle = true,
  noNestedScroll = false,
  compact = false,
  fullWidth = false,
  // Optional: transform data before submit (e.g., map virtual fields to real API fields)
  transformSubmitData,
  // Optional: hook invoked after any field change; can synchronously or asynchronously
  // request updates to other fields (e.g., when selecting equipo, prefill vencimientos)
  onFieldChange,
  // Optional: map backend field names to frontend field names for inline error display
  // e.g. { horas: 'lectura_contador', kilometros: 'lectura_contador' }
  errorKeyMap = {},
  // Optional: transform raw API response into virtual form fields (e.g. horas/km → lectura_contador)
  // Signature: (apiData, currentNormalized) => ({ fieldName: value, ... })
  transformFetchData,
  // Optional: oculta el botón "+" de alta rápida en campos con fkConfig/fkPath
  // (p. ej. portal mecánico, donde no debe poder crear tareas/fluidos/centros de costo/mecánicos)
  hideFkCreateButtons = false,
  // Optional: habilita los botones "Guardar y nuevo" / "Guardar y duplicar" en modo alta.
  // Los campos marcados con `preserveOnDuplicate: true` en `fields` se mantienen al duplicar;
  // el resto (y siempre los de tipo 'file') se vacía.
  enableSaveAndNew = false,
  // Optional: resuelve las opciones de un select desde otra fuente que no sea la API.
  // Lo usa la app móvil, que lee los catálogos de la copia guardada en el
  // dispositivo para que los formularios se completen sin conexión.
  // Firma: (optionsEndpoint, field) => Promise<Array|null>
  // Si devuelve un array se usa tal cual; si devuelve null/undefined, se sigue
  // con la carga por API de siempre. Así el formulario es el mismo en los dos
  // casos y no hay una copia que se desincronice.
  resolverOpciones,
  // Optional: reemplaza el POST/PATCH a la API por otra forma de guardar.
  // Lo usa la app móvil, que sin conexión encola el registro para enviarlo
  // cuando vuelva la señal. Firma: (payload, { esEdicion, id }) => Promise<{ data }>
  // Todo lo demás —validación, mapeo de errores del servidor, eventos— sigue
  // igual: lo único que cambia es a dónde va el dato.
  guardar,
  // Optional: pie de pantalla completa con los mismos botones grandes que
  // usan los demás formularios de la app móvil (combustible, parte diario),
  // en vez de los botones chicos alineados a la derecha del resto del
  // sistema. No cambia nada fuera de la app móvil — sólo lo activan
  // `LubricantesMovil` y `CorregirEnvio` para ese caso.
  botonesMovil = false,
}) => {
  // initialize formData safely (avoid null initialData)
  const [formData, setFormData] = useState(() => (initialData || {}));

  // Normalize fields once and accept `key` as alias for `name` (many callers use `key`)
  const normFields = React.useMemo(() => (
    Array.isArray(fields) ? fields.map(f => {
      const ff = { ...(f || {}), name: f.name || f.key };
      // accept legacy aliases: 'select_api' -> 'select', and 'endpoint' -> 'optionsEndpoint'
      if (ff.type === 'select_api') ff.type = 'select';
      if (!ff.optionsEndpoint && ff.endpoint) ff.optionsEndpoint = ff.endpoint + (ff.endpoint.endsWith('/') ? '' : '/');
      return ff;
    }) : []
  ), [fields]);

  // reuse the normalized list in-place so the rest of the file can continue using `fields`
  fields = normFields;

  const navigate = useNavigate();
  const location = useLocation();
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [fkModalKey, setFkModalKey] = useState(null);
  // submitting ref guard: prevents duplicate network calls if user clicks multiple times
  const submittingRef = useRef(false);
  // track if user modified the form (to avoid clobbering in-progress edits)
  const formPristineRef = useRef(true);
  const [selectOptions, setSelectOptions] = useState({});
  const [selectReloadTick, setSelectReloadTick] = useState(0);
  // Optional per-field client-side filters for searchable selects
  // remember the last applied initialData signature (id or serialized) to avoid
  // re-applying the same data on unrelated parent renders
  const lastInitialSigRef = useRef(null);
  const lastInitialIdRef = useRef(null);

  // Effective editing flag: consider editing when prop says so or when initialData/formData contains an id
  const effectiveIsEditing = Boolean(isEditing || initialData?.id || formData?.id);

  // Ensure formData updates when initialData prop changes (wrappers load async)
  // Normalize values: if a select field has an object as value, replace with its id;
  // also stringify values for consistent select matching.
  useEffect(() => {
    try {
      const normalized = { ...(initialData || {}) };

      // If we have fields available, normalize select-type fields
      if (Array.isArray(fields) && fields.length) {
        for (const f of fields) {
          if (f.type === 'select') {
            const raw = normalized[f.name];
            if (raw && typeof raw === 'object') {
              const id = raw.id || raw.value || '';
              normalized[f.name] = id !== undefined && id !== null ? String(id) : '';
            } else if (raw !== undefined && raw !== null) {
              normalized[f.name] = String(raw);
            } else {
              normalized[f.name] = '';
            }
          }
        }
      }

      // Build a cheap signature to detect meaningful changes to initialData.
      const sig = initialData && initialData.id ? `id:${initialData.id}` : JSON.stringify(initialData || {});
      const prevSig = lastInitialSigRef.current;

      // Only apply the incoming initialData when:
      // - the form is still pristine (user hasn't typed), OR
      // - the incoming initialData represents a different record (id changed)
      if (sig !== prevSig) {
        const prevId = lastInitialIdRef.current;
        const incomingId = initialData && initialData.id ? initialData.id : null;
        if (formPristineRef.current || (incomingId && incomingId !== prevId)) {
          console.debug('FormularioBase: initialData -> normalized formData', normalized, { fieldsCount: fields.length });
          setFormData(normalized);
          // reset pristine since we just populated the form from initial data
          formPristineRef.current = true;
        } else {
          console.debug('FormularioBase: skipping initialData apply because user modified form', { sig, prevSig });
        }
        lastInitialSigRef.current = sig;
        lastInitialIdRef.current = incomingId;
      }
    } catch (err) {
      console.warn('Error normalizing initialData for formData:', err);
      // Only set if the form is still pristine; avoid clobbering user edits
      if (formPristineRef.current) setFormData(initialData || {});
    }
  }, [initialData, fields]);

  // Safety net: si estamos editando y el wrapper no pasó correctamente los datos
  // (caso real: initialData.id existe pero formData está vacío o con campos vacíos),
  // solicitar el detalle al endpoint y poblar formData directamente.
  useEffect(() => {
    const shouldFetchDetail = () => {
      if (!effectiveIsEditing) return false;
      const id = initialData?.id || formData?.id;
      if (!id) return false;

      // If initialData already carries enough form fields, trust it and skip the fetch.
      // This prevents a race condition where formData is still {} when this effect fires
      // (because the sibling useEffect that syncs initialData → formData hasn't resolved
      // its state update yet), causing fetchDetail to overwrite correctly-set virtual
      // fields (e.g. lectura_contador) with empty strings from the raw API response.
      const nonIdInitKeys = Object.keys(initialData || {}).filter(k => k !== 'id').length;
      if (nonIdInitKeys >= Math.ceil(fields.length / 2)) return false;

      // consider it incomplete if formData has no keys or most fields are empty
      const keys = Object.keys(formData || {});
      if (keys.length === 0) return true;
      const emptyCount = fields.reduce((acc, f) => acc + ((formData[f.name] === undefined || formData[f.name] === '' ) ? 1 : 0), 0);
      // if more than half of fields are empty, refetch detail
      return emptyCount >= Math.ceil(fields.length / 2);
    };

    const fetchDetail = async () => {
      try {
        const id = initialData.id;
        if (!apiEndpoint || !id) return;
        let endpoint = apiEndpoint;
        if (!endpoint.endsWith('/')) endpoint += '/';
        endpoint = `${endpoint}${id}/`;
        const resp = await apiClient.get(endpoint);
        const data = resp.data || {};

        // Normalize into same shape expected by formData
        const normalized = { ...formData };
        for (const f of fields) {
          const raw = data[f.name];
          if (raw && typeof raw === 'object') {
            normalized[f.name] = String(raw.id || raw.value || '');
          } else if (raw !== undefined && raw !== null) {
            normalized[f.name] = String(raw);
          } else {
            // keep existing value if present
            normalized[f.name] = normalized[f.name] !== undefined ? normalized[f.name] : '';
          }
        }
        // include id if present
        if (data.id) normalized.id = data.id;
        // Allow forms with virtual fields (e.g. lectura_contador) to remap raw API
        // fields into their virtual counterparts after the base normalization.
        if (typeof transformFetchData === 'function') {
          try {
            const extra = transformFetchData(data, normalized);
            if (extra && typeof extra === 'object') Object.assign(normalized, extra);
          } catch (e) { /* ignore */ }
        }
        console.debug('FormularioBase: fetched detail and populated formData', { endpoint, normalized });
        setFormData(normalized);
      } catch (err) {
        console.warn('FormularioBase: error fetching detail for edit fallback:', err);
      }
    };

  if (shouldFetchDetail()) {
      fetchDetail();
    }
  }, [isEditing, initialData, apiEndpoint, fields, formData, effectiveIsEditing, transformFetchData]);

  // Cargar opciones para selects
  // Nota: re-ejecutar cuando cambian los fields o initialData para asegurar que,
  // al editar, si el valor guardado no está en la página actual (paginación del backend),
  // traigamos al menos ese registro individual para que el <select> muestre la opción seleccionada.
  useEffect(() => {
    const refreshOptions = () => setSelectReloadTick((prev) => prev + 1);
    window.addEventListener('focus', refreshOptions);
    window.addEventListener('gestion-taller:record-saved:confirmed', refreshOptions);

    return () => {
      window.removeEventListener('focus', refreshOptions);
      window.removeEventListener('gestion-taller:record-saved:confirmed', refreshOptions);
    };
  }, []);

  useEffect(() => {
    const loadSelectOptions = async () => {
      const optionsToLoad = {};

      // Preload selected option(s) from initialData so the UI shows the saved value
      // immediately while we fetch the full option lists.
      const preload = {};
      for (const field of fields) {
        if (!(field.type === 'select' && field.optionsEndpoint)) continue;
        const selectedRaw = initialData ? initialData[field.name] : undefined;
        const selectedId = selectedRaw && typeof selectedRaw === 'object' ? (selectedRaw.id || selectedRaw.value) : selectedRaw;
        if (!selectedId) continue;

        // Try to infer a label from common naming conventions on initialData
        let label = initialData[`${field.name}_nombre`] || initialData[`${field.name}_label`] || initialData[`${field.name}_display`] || '';
        // Common overrides for known FK fields present in list rows
        const commonMap = {
          equipo: ['equipo_interno', 'equipo_interno_nombre', 'equipo_nombre', 'equipo_descripcion'],
          tipo: ['tipo_nombre', 'tipo_label', 'tipo_display'],
          mecanico: ['mecanico_nombre', 'mecanico_label']
        };
        if (commonMap[field.name]) {
          for (const k of commonMap[field.name]) {
            if (!label && initialData[k]) label = initialData[k];
          }
        }
        if (!label && typeof selectedRaw === 'object') {
          label = selectedRaw.nombre || selectedRaw.label || selectedRaw.name || '';
        }
        if (!label) label = 'Cargando...';

  preload[field.name] = [{ id: selectedId, value: selectedId, nombre: label, interno: initialData.equipo_interno, descripcion: initialData.equipo_descripcion }];
      }

      if (Object.keys(preload).length) {
        setSelectOptions(prev => ({ ...(prev || {}), ...preload }));
      }

      for (const field of fields) {
        if (field.type === 'select' && field.optionsEndpoint) {
          try {
            // If the endpoint is equipos/, try to load the special endpoint that returns all equipos
            let opts = [];

            // Resolutor propio primero (app móvil: lee la copia local de los
            // catálogos). Si devuelve una lista, ni se toca la red — que es el
            // punto, porque en la obra no hay. Si devuelve null, sigue el
            // camino de siempre.
            let opcionesLocales = null;
            if (typeof resolverOpciones === 'function') {
              try {
                opcionesLocales = await resolverOpciones(field.optionsEndpoint, field);
              } catch (errResolver) {
                console.error(`Error resolviendo opciones locales para ${field.name}:`, errResolver);
              }
            }

            if (Array.isArray(opcionesLocales)) {
              opts = opcionesLocales;
            } else {
              try {
                if (field.optionsEndpoint && field.optionsEndpoint.replace(/\/+$/, '').includes('equipos')) {
                  const respAll = await apiClient.get('/equipos/todos-para-arbol/');
                  opts = respAll.data.results || respAll.data || [];
                } else {
                  const response = await apiClient.get(field.optionsEndpoint);
                  opts = response.data.results || response.data || [];
                }
              } catch (errLoad) {
                // Fallback to single page request if special endpoint fails
                try {
                  const response = await apiClient.get(field.optionsEndpoint);
                  opts = response.data.results || response.data || [];
                } catch (err2) {
                  console.error(`Error loading options for ${field.name}:`, err2);
                  opts = [];
                }
              }
            }

            // Detectar valor seleccionado en initialData (puede ser id o objeto)
            const selectedRaw = initialData ? initialData[field.name] : undefined;
            const selectedId = selectedRaw && typeof selectedRaw === 'object' ? (selectedRaw.id || selectedRaw.value) : selectedRaw;

            // Debug: log loaded options info for equipo (helps diagnosing blank edits)
            try {
              if (field.name === 'equipo') {
                console.debug(`FormularioBase: opciones cargadas para '${field.name}'`, { count: opts.length, first: opts[0] });
                console.debug(`FormularioBase: selectedRaw for ${field.name}:`, selectedRaw, 'selectedId:', selectedId);
              }
            } catch (dbgErr) {
              /* ignore debug errors */
            }

            // Si estamos editando y hay un selectedId, asegurarnos que esté dentro de las opciones.
            if (selectedId && !opts.some(o => String(o.id || o.value) === String(selectedId))) {
              try {
                // Construir endpoint para obtener el objeto individual.
                // field.optionsEndpoint suele ser algo como 'equipos/' -> queremos 'equipos/{id}/'
                let singleEndpoint = field.optionsEndpoint;
                if (!singleEndpoint.endsWith('/')) singleEndpoint += '/';
                singleEndpoint = `${singleEndpoint}${selectedId}/`;

                const singleResp = await apiClient.get(singleEndpoint);
                const singleObj = singleResp.data;
                // Prepend para que aparezca como primera opción
                opts = [singleObj].concat(opts);
              } catch (errSingle) {
                // Si falla, no romper todo; ya cargamos la primera página
                console.warn(`No se pudo cargar opción individual para ${field.name} id=${selectedId}:`, errSingle);
              }
            }

            optionsToLoad[field.name] = opts;
          } catch (error) {
            console.error(`Error loading options for ${field.name}:`, error);
            optionsToLoad[field.name] = [];
          }
        }
      }

      setSelectOptions(optionsToLoad);
    };

    loadSelectOptions();
  }, [fields, initialData, selectReloadTick, resolverOpciones]);

  // Manejar cambios en el formulario
  const handleChange = (name, value) => {
    // mark form as modified by the user
    formPristineRef.current = false;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpiar error si existe
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }

    // Allow parent to react to a field change and update other fields
    if (typeof onFieldChange === 'function') {
      try {
        const nextData = { ...formData, [name]: value };
        const applyUpdates = (updates) => {
          if (updates && typeof updates === 'object') {
            setFormData(prev => ({ ...prev, ...updates }));
          }
        };
        const maybePromise = onFieldChange(name, value, nextData, applyUpdates);
        if (maybePromise && typeof maybePromise.then === 'function') {
          maybePromise.then(applyUpdates).catch(() => {/* ignore */});
        }
      } catch (e) {
        // ignore onFieldChange errors to not block typing
      }
    }
  };

  // Validar formulario
  const validateForm = () => {
    const newErrors = {};
    
    fields.forEach(field => {
      const value = formData[field.name];
      const rules = validationRules[field.name];
      
      if (rules) {
        if (rules.required && (!value || value.toString().trim() === '')) {
          newErrors[field.name] = `${field.label} es requerido`;
        }
        
        if (rules.minLength && value && value.length < rules.minLength) {
          newErrors[field.name] = `${field.label} debe tener al menos ${rules.minLength} caracteres`;
        }
        
        if (rules.maxLength && value && value.length > rules.maxLength) {
          newErrors[field.name] = `${field.label} no puede tener más de ${rules.maxLength} caracteres`;
        }
        
        if (rules.pattern && value && !rules.pattern.test(value)) {
          newErrors[field.name] = rules.message || `${field.label} tiene un formato inválido`;
        }
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Construye un formData vacío según `fields` (mismo criterio que la carga inicial).
  const buildBlankFormData = () => {
    const blank = {};
    fields.forEach(f => { blank[f.name] = f.type === 'checkbox' ? false : ''; });
    return blank;
  };

  // Parte del formData actual y limpia todo campo que no esté marcado para
  // preservarse al duplicar (y siempre los adjuntos, que son únicos por naturaleza).
  const buildDuplicateFormData = (prev) => {
    const next = { ...prev };
    delete next.id;
    fields.forEach(f => {
      if (f.type === 'file' || !f.preserveOnDuplicate) {
        next[f.name] = f.type === 'checkbox' ? false : '';
      }
    });
    return next;
  };

  // Manejar envío del formulario
  // `opts.after`: 'close' (default, comportamiento actual) | 'new' | 'duplicate'
  const handleSubmit = async (e, opts = {}) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    const after = opts.after || 'close';

    // Prevent re-entrancy: ignore additional submits while one is in-flight
    if (submittingRef.current) {
      return;
    }

    if (!validateForm()) {
      return;
    }
    
    // mark submitting immediately (ref is sync)
    submittingRef.current = true;
    setLoading(true);
    // Clear any previous server errors
    setErrors({});

    try {
      // Detectar si hay campos de archivo -> usar FormData
      const hasFileField = fields.some(f => f.type === 'file');

      // Apply optional transform before shipping to server
      const buildPayload = () => {
        const source = { ...formData };
        if (typeof transformSubmitData === 'function') {
          try {
            const transformed = transformSubmitData(source);
            if (transformed && typeof transformed === 'object') return transformed;
          } catch (e) {
            // fall back to source on error
          }
        }
        return source;
      };

      let resp;
      if (typeof guardar === 'function') {
        // Guardado inyectado (app móvil). Va antes que la rama de archivos a
        // propósito: los formularios de carga en obra no adjuntan archivos, y
        // si alguna vez lo hicieran, un FormData no se puede serializar a la
        // cola de envíos — mejor que falle acá y no en silencio.
        resp = await guardar(buildPayload(), {
          esEdicion: effectiveIsEditing,
          id: formData.id,
        });
      } else if (hasFileField) {
        const submitData = new FormData();
        const payload = buildPayload();
        Object.keys(payload).forEach(key => {
          if (payload[key] === null || payload[key] === undefined) return;
          if (payload[key] instanceof File) {
            submitData.append(key, payload[key]);
          } else if (typeof payload[key] === 'object') {
            submitData.append(key, JSON.stringify(payload[key]));
          } else {
            submitData.append(key, payload[key]);
          }
        });
        if (effectiveIsEditing) {
          // Sin Content-Type para que el browser auto-detecte multipart/form-data con boundary
          resp = await apiClient.patch(`${apiEndpoint}${formData.id}/`, submitData, {
            headers: { 'Content-Type': undefined },
          });
        } else {
          resp = await apiClient.post(apiEndpoint, submitData, {
            headers: { 'Content-Type': undefined },
          });
        }
      } else {
        const payload = buildPayload();
        if (effectiveIsEditing) {
          resp = await apiClient.patch(`${apiEndpoint}${formData.id}/`, payload);
        } else {
          resp = await apiClient.post(apiEndpoint, payload);
        }
      }

      // SUCCESS: notify listeners, then close modal
      try {
        window.dispatchEvent(new CustomEvent('gestion-taller:record-saved', {
          detail: { data: resp?.data, endpoint: apiEndpoint, isEditing: effectiveIsEditing }
        }));
        window.dispatchEvent(new CustomEvent('gestion-taller:record-saved:confirmed', {
          detail: { data: resp?.data, endpoint: apiEndpoint, isEditing: effectiveIsEditing }
        }));
      } catch {}

      if (after === 'close') {
        if (onSave) {
          onSave();
        } else if (onCancel) {
          onCancel();
        } else {
          window.history.back();
        }
      } else {
        // 'new' o 'duplicate': avisar al padre (para que refresque listas) sin
        // que cierre el formulario, y quedarnos acá con el próximo registro listo.
        // formPristineRef queda en false a propósito: así el efecto que sincroniza
        // initialData -> formData no vuelve a pisar este reset con los datos del
        // registro recién guardado (initialData del wrapper no cambia en este flujo).
        if (typeof onSave === 'function') onSave(resp?.data, { keepOpen: true });
        setFormData(after === 'new' ? buildBlankFormData() : buildDuplicateFormData(formData));
      }

    } catch (error) {
      console.error('FormularioBase: error al guardar:', error);

      // Map DRF validation errors to per-field state so they appear inline
      const responseData = error?.response?.data;
      if (responseData && typeof responseData === 'object' && !Array.isArray(responseData)) {
        const rawErrors = {};
        for (const [key, val] of Object.entries(responseData)) {
          const msg = Array.isArray(val) ? val.join(' ') : String(val);
          if (key === 'non_field_errors' || key === 'detail') {
            rawErrors.__form = msg;
          } else {
            rawErrors[key] = msg;
          }
        }
        // Apply errorKeyMap: remap backend field names to frontend field names
        const newErrors = {};
        for (const [k, v] of Object.entries(rawErrors)) {
          const mappedKey = (errorKeyMap && errorKeyMap[k]) ? errorKeyMap[k] : k;
          newErrors[mappedKey] = newErrors[mappedKey] ? newErrors[mappedKey] + ' ' + v : v;
        }
        setErrors(Object.keys(newErrors).length ? newErrors : { __form: 'Error al guardar. Intente nuevamente.' });
      } else {
        setErrors({ __form: 'Error al guardar. Intente nuevamente.' });
      }
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  };

  // Al abrir el formulario, el cursor va al primer campo: así se puede empezar a
  // escribir de una, sin buscarlo con el mouse. En una carga de muchos registros
  // seguidos ese gesto se repetía una vez por registro.
  const formRef = useRef(null);
  useAutoFocoPrimerCampo(formRef, !loading);

  // Renderizar campo según tipo
  const renderField = (field) => {
    const hasError = errors[field.name];
    const rawValue = formData[field.name];
    const value = (rawValue !== undefined && rawValue !== null) ? String(rawValue) : '';

    // Alta rápida de la FK que falta. La comparten el botón "+" y el atajo Alt+N,
    // así que las dos vías hacen exactamente lo mismo: si mañana cambia el flujo
    // de alta, no puede quedar una de las dos con el comportamiento viejo.
    // Devuelve null cuando el campo no admite alta rápida.
    const abrirAltaFk = (() => {
      if (hideFkCreateButtons) return null;
      if (field.fkPath) {
        return () => {
          // Navegar al formulario completo y volver al terminar.
          const returnTo = location.pathname + location.search;
          const returnState = {
            returnContext: {
              sourcePath: location.pathname,
              sourceSearch: location.search,
              sourceField: field.name,
              sourceRecordId: formData?.id || null,
              sourceFormData: formData,
            },
          };
          navigate(field.fkPath, { state: { returnTo, returnState } });
        };
      }
      if (!field.fkConfig) return null;
      return () => setFkModalKey(field.name);
    })();

    // Alt+N sobre el campo enfocado abre el alta de esa FK. Existe porque el
    // botón "+" quedó fuera del recorrido de Tab: sin atajo, la única forma de
    // llegar sería el mouse, y la idea es no tener que soltar el teclado.
    // Alt+N no pisa ningún atajo del navegador (Ctrl+N sí abriría una ventana).
    const manejarAtajoCampo = (evento) => {
      if (!abrirAltaFk) return;
      if (evento.altKey && !evento.ctrlKey && !evento.metaKey && (evento.key === 'n' || evento.key === 'N')) {
        evento.preventDefault();
        abrirAltaFk();
      }
    };

    const renderCreateButton = () => {
      if (!abrirAltaFk) return null;
      return (
        <AddSelectButton
          type="button"
          // Fuera del recorrido de Tab: es un atajo ocasional (dar de alta la FK
          // que falta), no un paso de la carga. Con tabIndex 0 costaba un Tab de
          // más en CADA campo FK, que en una carga de veinte registros es la
          // diferencia entre fluido y tedioso. Se sigue pudiendo usar con el
          // mouse y con Alt+N desde el campo (ver manejarAtajoCampo).
          tabIndex={-1}
          title={`Agregar ${field.label} (Alt+N)`}
          onClick={abrirAltaFk}
        >
          <i className="bi bi-plus-lg" aria-hidden="true" />
        </AddSelectButton>
      );
    };

    switch (field.type) {
      case 'file': {
        const removeMarked = rawValue === '__REMOVE__';
        const currentFileUrl = field.currentFileUrlField ? formData[field.currentFileUrlField] : null;
        const currentFileName = (field.currentFileNameField && formData[field.currentFileNameField]) || 'Ver archivo actual';

        if (currentFileUrl && !removeMarked && !(rawValue instanceof File)) {
          return (
            <CurrentFileRow>
              <CurrentFileLink href={currentFileUrl} target="_blank" rel="noopener noreferrer">
                <i className="fas fa-file-pdf" aria-hidden="true" />
                {currentFileName}
              </CurrentFileLink>
              <RemoveFileButton type="button" onClick={() => handleChange(field.name, '__REMOVE__')}>
                Eliminar
              </RemoveFileButton>
            </CurrentFileRow>
          );
        }

        return (
          <>
            <Input
              id={field.name}
              type="file"
              onChange={(e) => handleChange(field.name, e.target.files[0] || null)}
              placeholder={field.placeholder}
              $error={hasError}
            />
            {removeMarked && (
              <ErrorMessage as="div" style={{ color: 'inherit' }}>
                El archivo actual se eliminará al guardar. Podés cargar uno nuevo o dejarlo vacío.
              </ErrorMessage>
            )}
          </>
        );
      }
      case 'select':
        const options = selectOptions[field.name] || field.options || [];
        const filteredOptions = field.name === 'sector' && formData.obra
          ? options.filter(option => String(option.obra || option.obra_id || '') === String(formData.obra))
          : options;
        return (
          // El atajo va en la fila y no en el input: el evento burbujea desde
          // el campo enfocado, y así funciona igual sin importar qué combo se
          // esté usando adentro.
          <InlineRow onKeyDown={manejarAtajoCampo}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <SearchableSelect
                options={filteredOptions}
                currentValue={value}
                field={field}
                formData={formData}
                handleChange={handleChange}
                resolverOpciones={resolverOpciones}
                selectOptions={selectOptions}
                hasError={hasError}
              />
            </div>
            {renderCreateButton()}
          </InlineRow>
        );

      case 'textarea':
        return (
          <TextArea
            id={field.name}
            value={value}
            onChange={(e) => handleChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            $error={hasError}
          />
        );
      case 'checkbox':
        return (
          <InlineRow>
            <input
              id={field.name}
              type="checkbox"
              checked={Boolean(rawValue)}
              onChange={(e) => handleChange(field.name, e.target.checked)}
            />
            <Label htmlFor={field.name} style={{ margin: 0 }}>{field.label}</Label>
          </InlineRow>
        );
        
      default:
        return (
          <Input
            id={field.name}
            type={field.type || 'text'}
            value={value}
            onChange={(e) => handleChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            $error={hasError}
            disabled={typeof field.disabled === 'function' ? field.disabled(formData) : Boolean(field.disabled)}
          />
        );
    }
  };

  return (
  <FormContainer $noNestedScroll={noNestedScroll} $compact={compact} $fullWidth={fullWidth}>
      {showTitle && (
        <FormTitle>
          {icon && <i className={icon}></i>}
          {title}
        </FormTitle>
      )}

      <form ref={formRef} onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
    <ScrollArea $noNestedScroll={noNestedScroll} $compact={compact}>
          <FormGrid>
            {fields.map(field => (
              <FormGroup key={field.name} style={field.fullWidth ? { gridColumn: '1 / -1' } : undefined}>
                  {field.type !== 'checkbox' && (
                    <Label htmlFor={field.name}>{field.label}</Label>
                  )}
                  {renderField(field)}
                {errors[field.name] && (
                  <ErrorMessage>{errors[field.name]}</ErrorMessage>
                )}
              </FormGroup>
            ))}
          </FormGrid>
        </ScrollArea>

        {errors && errors.__form && (
          <div style={{ padding: '0 1.5rem 0.5rem' }}>
            <ErrorMessage>{errors.__form}</ErrorMessage>
          </div>
        )}
        <ButtonGroup $compact={compact} $botonesMovil={botonesMovil}>
          {botonesMovil ? (
            <FilaBotonesPie>
              <BotonCancelarPie type="button" onClick={onCancel || (() => window.history.back())}>
                Cancelar
              </BotonCancelarPie>
              <BotonGuardarPie type="submit" disabled={loading}>
                {loading ? 'Guardando...' : (isEditing ? 'Actualizar' : 'Guardar')}
              </BotonGuardarPie>
            </FilaBotonesPie>
          ) : (
            <>
              {enableSaveAndNew && !effectiveIsEditing && (
                <>
                  <DuplicarButton
                    type="button"
                    title="Guardar y crear otro con los mismos datos (los campos únicos quedan vacíos)"
                    disabled={loading}
                    onClick={() => handleSubmit(null, { after: 'duplicate' })}
                  >
                    <i className="bi bi-copy" style={{ fontSize: '0.75rem' }} />
                    Guardar y duplicar
                  </DuplicarButton>
                  <NavButton
                    type="button"
                    title="Guardar y abrir un formulario en blanco para cargar otro"
                    disabled={loading}
                    onClick={() => handleSubmit(null, { after: 'new' })}
                  >
                    <i className="bi bi-file-earmark-plus" style={{ fontSize: '0.75rem' }} />
                    Guardar y nuevo
                  </NavButton>
                </>
              )}
              <SecondaryButton type="button" onClick={onCancel || (() => window.history.back())}>
                Cancelar
              </SecondaryButton>
              <PrimaryButton type="submit" disabled={loading}>
                {loading ? 'Guardando...' : (isEditing ? 'Actualizar' : 'Guardar')}
              </PrimaryButton>
            </>
          )}
        </ButtonGroup>
      </form>

      {fkModalKey && (() => {
        const activeField = fields.find(f => f.name === fkModalKey);
        const cfg = activeField?.fkConfig;
        if (!cfg) return null;
        return (
          <ConfiguracionBase
            apiEndpoint={cfg.apiEndpoint}
            titulo={cfg.titulo}
            descripcion={cfg.descripcion || ''}
            icon={cfg.icon || ''}
            embedded
            compact
            modalOnly
            autoOpenAdd
            onRecordSaved={(savedRecord) => {
              if (!savedRecord?.id) { setFkModalKey(null); return; }
              setSelectOptions(prev => ({
                ...prev,
                [fkModalKey]: [...(prev[fkModalKey] || []).filter(o => String(o.id) !== String(savedRecord.id)), savedRecord],
              }));
              handleChange(fkModalKey, String(savedRecord.id));
              setFkModalKey(null);
            }}
            onRequestClose={() => setFkModalKey(null)}
          />
        );
      })()}
    </FormContainer>
  );
};

export default FormularioBase;
